// All user-facing dates render as mm/dd/yyyy for every viewer, regardless of
// browser locale. Change APP_LOCALE (not individual call sites) to move the
// whole app to another format.
export const APP_LOCALE = "en-US";

const DATE_OPTS = { year: "numeric", month: "2-digit", day: "2-digit" };
const TIME_OPTS = { hour: "2-digit", minute: "2-digit" };

// Date-only strings ("2026-08-19") are calendar facts, not instants. Passing one
// to `new Date()` makes JS read it as UTC midnight, which renders as the
// previous day in every US timezone — so pin those to local midnight instead.
const toDate = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

/** "08/19/2026" */
export const formatDate = (value, fallback = "-") => {
  if (!value) return fallback;
  const d = toDate(value);
  return isNaN(d.getTime())
    ? fallback
    : d.toLocaleDateString(APP_LOCALE, DATE_OPTS);
};

/** "08/19/2026, 02:30 PM" */
export const formatDateTime = (value, fallback = "-") => {
  if (!value) return fallback;
  const d = toDate(value);
  return isNaN(d.getTime())
    ? fallback
    : d.toLocaleString(APP_LOCALE, { ...DATE_OPTS, ...TIME_OPTS });
};

/**
 * The PO DATE cell: "08/19/2026".
 *
 * `po_date` is the real field, but it only exists on POs written after it was
 * added — everything older carries nothing until
 * scripts/backfill-asn-po-date.js has run, and an ASN read comes back lean
 * (feathers-mongoose defaults `lean: true`), so the model's `default: Date.now`
 * is NOT applied on the way out. Without a fallback those rows render blank.
 *
 * The fallback chain deliberately matches the backfill's precedence exactly
 * (`others.purchase_order_date` -> `createdAt`), so a PO shows the SAME date
 * before and after the backfill runs. Chaining straight to `createdAt` would be
 * simpler but wrong for provider-imported POs: they would show their import
 * date now and their true PO date later, and look like the data had changed.
 *
 * Sorting does not follow this — Mongo orders on the stored `po_date` alone —
 * so a PO with no `po_date` still sorts to the end of the list until the
 * backfill has run. Display resilience only.
 */
export const formatPoDate = (asn, fallback = "-") =>
  formatDate(
    asn?.po_date || asn?.others?.purchase_order_date || asn?.createdAt,
    fallback,
  );

/**
 * When a trail entry actually happened, for the tracking steppers.
 *
 * Both trail models carry a real event time alongside the row's own
 * `createdAt`, and they spell it differently:
 *
 *   asn-trail.model.js    `date` (Date)   — control-tower's tracking-subscriber
 *                                           stores the CARRIER's own
 *                                           `event_date_time` here.
 *   order-trail.model.js  `time` (String) — utils/order-modify.js stores the
 *                                           source event's `created_at` here.
 *
 * `createdAt` is only when WE wrote the row, so it drifts from the truth
 * exactly when it matters most: a backfill, an outage catch-up, or a batch
 * sync all stamp "now" on events that happened days ago. Prefer the real time
 * and keep `createdAt` as the floor.
 *
 * The fallback is not optional: rows written before `date`/`time` existed have
 * neither. Every call site had its own version of this chain — `time ||
 * createdAt`, `date || createdAt`, or just `createdAt` — which is why the PO
 * stepper and the asn-tracking timeline disagreed about the same trail row.
 *
 * Unparseable values are skipped rather than returned, because `time` is a
 * free String and a bad carrier timestamp would otherwise reach
 * getFormattedDate, which has no NaN guard and would print "NaN:NaN".
 */
export const trailEventDate = (parent) => {
  for (const value of [parent?.date, parent?.time, parent?.createdAt]) {
    if (!value) continue;
    if (!isNaN(toDate(value).getTime())) return value;
  }
  return undefined;
};

export const getFormattedDate = (date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const d = new Date(date)
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const time = `${hours}:${minutes}`
  const dayName = days[d.getDay()]
  const day = d.getDate()
  const year = d.getFullYear()
  const monthName = months[d.getMonth()]
  const dateStr = `${dayName} ${day} ${monthName}`
  const yearStr = year
  return { time, dateStr, yearStr }
}
